import { IAddress } from "@/core/types/address";
import { dayjs } from "@/core/utils/date";
import { DetailedUser } from "@/user/user";
import { IReferral } from "./referral";

export const REFERRAL_COLUMN = {
  new: "new",
  inProgress: "inProgress",
  scheduled: "scheduled",
  closed: "closed",
} as const;

export const REFERRAL_STATUS = {
  LEAD: "LEAD",
  IN_CONTACT: "IN_CONTACT",
  SCHEDULED: "SCHEDULED",
  CLOSED: "CLOSED",
  PAID: "PAID",
  LOST: "LOST",
} as const;

export const REFERRAL_COLUMN_TO_HEADER = {
  [REFERRAL_COLUMN.new]: "New referral",
  [REFERRAL_COLUMN.inProgress]: "In progress",
  [REFERRAL_COLUMN.scheduled]: "Scheduled",
  [REFERRAL_COLUMN.closed]: "Closed",
} as const;

export const REFERRAL_COLUMN_TO_STATUS = {
  [REFERRAL_COLUMN.new]: REFERRAL_STATUS.LEAD,
  [REFERRAL_COLUMN.inProgress]: REFERRAL_STATUS.IN_CONTACT,
  [REFERRAL_COLUMN.scheduled]: REFERRAL_STATUS.SCHEDULED,
  [REFERRAL_COLUMN.closed]: REFERRAL_STATUS.CLOSED,
} as const;

export const REFERRAL_STATUS_TO_COLUMN = {
  [REFERRAL_STATUS.LEAD]: REFERRAL_COLUMN.new,
  [REFERRAL_STATUS.IN_CONTACT]: REFERRAL_COLUMN.inProgress,
  [REFERRAL_STATUS.SCHEDULED]: REFERRAL_COLUMN.scheduled,
  [REFERRAL_STATUS.CLOSED]: REFERRAL_COLUMN.closed,
} as const;

export const REFFERRAL_STATUS_TO_SIDEBAR_TEXT = {
  [REFERRAL_STATUS.LEAD]: "New",
  [REFERRAL_STATUS.IN_CONTACT]: "In progress",
  [REFERRAL_STATUS.SCHEDULED]: "Scheduled",
  [REFERRAL_STATUS.CLOSED]: "Closed",
  [REFERRAL_STATUS.LOST]: "Lost",
  [REFERRAL_STATUS.PAID]: "Paid",
} as const;

export const REFERRAL_COLUMN_TO_COLOR = {
  [REFERRAL_COLUMN.new]: "#d5f6ee",
  [REFERRAL_COLUMN.inProgress]: "#d5dff6",
  [REFERRAL_COLUMN.scheduled]: "#ead5f6",
  [REFERRAL_COLUMN.closed]: "#d5f6ee",
} as const;

export const REFERRAL_FIELD = {
  service_needed: "service_needed",
  assigned_reps: "assigned_reps",
  additional_info: "additional_info",
  address: "address",
  appointment_date: "appointment_date",
  deal_size: "deal_size",
  created_at: "created_at",
  status: "status",
} as const;

export const REFERRAL_FIELD_TO_INITIAL_VALUE = {
  [REFERRAL_FIELD.service_needed]: (value: string | null) => value || "",
  [REFERRAL_FIELD.assigned_reps]: (value: DetailedUser[]) =>
    value && value.length > 0 ? value[0].id : "",
  [REFERRAL_FIELD.additional_info]: (value: string | null) => value || "",
  [REFERRAL_FIELD.address]: (value: IAddress | null) =>
    value || {
      line_one: "",
      line_two: "",
      city: "",
      state: "",
      zip_code: "",
    },
  [REFERRAL_FIELD.appointment_date]: (value: string | null) => value || "",
  [REFERRAL_FIELD.deal_size]: (value: number | null) => value?.toString() || "",
};

export const REFERRAL_FIELD_TO_UPDATE_FIELD = {
  [REFERRAL_FIELD.service_needed]: "service_needed",
  [REFERRAL_FIELD.assigned_reps]: "representative_assigned_id",
  [REFERRAL_FIELD.additional_info]: "additional_info",
  [REFERRAL_FIELD.address]: "address",
  [REFERRAL_FIELD.appointment_date]: "appointment_date",
  [REFERRAL_FIELD.deal_size]: "deal_size",
};

export const REFERRAL_FIELD_TO_LABEL = {
  [REFERRAL_FIELD.service_needed]: "Services Needed",
  [REFERRAL_FIELD.additional_info]: "Notes",
  [REFERRAL_FIELD.address]: "Home Address",
  [REFERRAL_FIELD.appointment_date]: "Appointment Date",
  [REFERRAL_FIELD.deal_size]: "Deal Size",
};

export const COLUMN_TO_UPDATE_MODAL_INFO = {
  [REFERRAL_COLUMN.new]: null,
  [REFERRAL_COLUMN.inProgress]: {
    title: "Move to In Progress",
    status: REFERRAL_COLUMN_TO_STATUS.inProgress,
    requiredFields: [REFERRAL_FIELD.service_needed],
    constantFormFields: [
      REFERRAL_FIELD.service_needed,
      REFERRAL_FIELD.additional_info,
    ],
    ifNeededFormFields: [],
  },
  [REFERRAL_COLUMN.scheduled]: {
    title: "Move to Scheduled",
    status: REFERRAL_COLUMN_TO_STATUS.scheduled,
    requiredFields: [REFERRAL_FIELD.service_needed],
    ifNeededFormFields: [REFERRAL_FIELD.service_needed],
    constantFormFields: [
      REFERRAL_FIELD.address,
      REFERRAL_FIELD.appointment_date,
      REFERRAL_FIELD.additional_info,
    ],
  },
  [REFERRAL_COLUMN.closed]: {
    title: "Move to Closed",
    status: REFERRAL_COLUMN_TO_STATUS.closed,
    requiredFields: [REFERRAL_FIELD.service_needed, REFERRAL_FIELD.deal_size],
    ifNeededFormFields: [REFERRAL_FIELD.service_needed],
    constantFormFields: [
      REFERRAL_FIELD.deal_size,
      REFERRAL_FIELD.additional_info,
    ],
  },
};

const REFERRAL_STATUS_TO_ORDER = {
  LEAD: 0,
  IN_CONTACT: 1,
  SCHEDULED: 2,
  CLOSED: 3,
  PAID: 4,
  LOST: 5,
} as const;

export const REFERRAL_FIELD_TO_SORTER = {
  [REFERRAL_FIELD.created_at]: (a: IReferral, b: IReferral) =>
    dayjs(a.created_at).diff(dayjs(b.created_at)),
  [REFERRAL_FIELD.status]: (a: IReferral, b: IReferral) => {
    // First sort by status
    const statusDiff =
      REFERRAL_STATUS_TO_ORDER[a.status] - REFERRAL_STATUS_TO_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;

    // Then sort by creation date within each status group (newest to oldest)
    return -dayjs(a.created_at).diff(dayjs(b.created_at));
  },
};

export const REFERRAL_STATUS_TO_BADGE_VARIANT = {
  [REFERRAL_STATUS.LEAD]: "green",
  [REFERRAL_STATUS.IN_CONTACT]: "blue",
  [REFERRAL_STATUS.SCHEDULED]: "purple",
  [REFERRAL_STATUS.CLOSED]: "gold",
  [REFERRAL_STATUS.LOST]: "red",
  [REFERRAL_STATUS.PAID]: "orange",
} as const;

export const ACTIVE_REFERRAL_STATUSES = [
  REFERRAL_STATUS.LEAD,
  REFERRAL_STATUS.IN_CONTACT,
  REFERRAL_STATUS.SCHEDULED,
  REFERRAL_STATUS.CLOSED,
] as string[];
